import { Info } from 'components/ui/Alert/Info/Info';
import { Button } from 'components/ui/Button/Button';
import { CHINA_COUNTRY_CODE, IN_COUNTRY_CODE } from 'constants/index';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { shouldDisplayAddCoupon } from 'selectors';
import {
  getForeignOffers,
  getLocalOffers,
} from 'selectors/entities/offers/offers';
import { useTranslations } from 'utils/translations/useTranslations';
import { LoadingDots } from '../../LoadingDots/LoadingDots';
import { Coupon } from '../components/Coupon/Coupon';
import { ReactComponent as InfoIcon } from '../components/Option/assets/ico-info-blue.svg';
import { OptionList } from '../components/OptionList/OptionList';
import { Terms } from '../components/Terms/Terms';
import { usePaymentOptions } from '../hooks/usePaymentOptions';
import './SinglePaymentOptions.scss';

type SinglePaymentOptionsProps = {
  isFetchingOffer: boolean;
  isOfferAstropay?: boolean;
  selectOption: (name: string, value: string) => void;
  countryCode: string;
  onNext: () => void;
  hideNonFxOptions?: boolean;
};

export const SinglePaymentOptions = ({
  isFetchingOffer,
  isOfferAstropay = false,
  selectOption,
  countryCode,
  onNext,
  hideNonFxOptions = false,
}: SinglePaymentOptionsProps) => {
  const i18n = useTranslations();

  const [isCouponEnabled, setCouponEnabled] = useState(false);
  const localOptions = useSelector(getLocalOffers);
  const foreignOptions = useSelector(getForeignOffers);
  const isCouponChecked = useSelector(shouldDisplayAddCoupon);
  const [foreignsVisible, setForeignsVisible] = useState(false);

  const handleClickNext = () => {
    onNext();
  };

  const {
    dirtyFields,
    errors,
    handleFieldChange,
    handleOnClick,
    handleSelectOffer,
    handleCouponChange,
    handleTermsChange,
    isSubmitting,
    isValid,
    selectedOption,
    values,
  } = usePaymentOptions({
    onClick: handleClickNext,
    selectOption,
  });

  useEffect(() => {
    if (
      selectedOption?.id &&
      foreignOptions.find(option => option.id === selectedOption.id)
    ) {
      setForeignsVisible(true);
    }
  }, [selectedOption, foreignOptions]);

  useEffect(() => {
    const isCouponsForChinaOrIndia =
      IN_COUNTRY_CODE === countryCode || CHINA_COUNTRY_CODE === countryCode;
    const isSurchargePaymentMethod = selectedOption?.surcharge || false;
    setCouponEnabled(isCouponsForChinaOrIndia && !isSurchargePaymentMethod);
  }, [selectedOption, countryCode]);

  function handleShowForeignsClick() {
    setForeignsVisible(!foreignsVisible);
  }

  const hasForeignOptions = foreignOptions.length > 0;
  const hasLocalOptions = localOptions.length > 0;
  const hasOnlyForeignOptions = !hasLocalOptions && hasForeignOptions;

  const renderOnlyForeignOptionsAlert = () => (
    <div
      className="PaymentOptions-noLocalOptions"
      data-testid="only-foreign-alert"
    >
      <Info>
        <span className="info-icon">
          <InfoIcon aria-hidden />
        </span>
        {i18n.t('paymentOptions.noLocalOffers.description')}
      </Info>
    </div>
  );

  const renderForeignOptions = () => (
    <>
      {hasLocalOptions && (
        <p
          className="PaymentOptions-disclaimer"
          data-testid="foreign-disclaimer"
        >
          {i18n.t('checkout.paymentOptions.disclaimer')}
        </p>
      )}
      {hasOnlyForeignOptions && renderOnlyForeignOptionsAlert()}
      <ul
        className="PaymentOptions-list"
        aria-label={i18n.t('paymentOptions.foreignOptions') as string}
        data-testid="paymentOptions-foreign"
      >
        <OptionList
          options={foreignOptions}
          errors={errors}
          dirtyFields={dirtyFields}
          isFetchingOffer={isFetchingOffer}
          onChange={handleSelectOffer}
          onFieldChange={handleFieldChange}
          values={values}
        />
      </ul>
    </>
  );

  return (
    <>
      <div
        className="PaymentOptions flex flex-col justify-between h-full"
        data-testid="single-payment-options"
      >
        <div>
          <div id="PaymentOptions-local">
            <ul
              className="PaymentOptions-list"
              aria-label={i18n.t('paymentOptions.localOptions') as string}
              data-testid="paymentOptions-local"
            >
              <OptionList
                options={localOptions}
                errors={errors}
                dirtyFields={dirtyFields}
                isFetchingOffer={isFetchingOffer}
                onChange={handleSelectOffer}
                onFieldChange={handleFieldChange}
                values={values}
              />
            </ul>
          </div>
          {hasForeignOptions && !hideNonFxOptions && (
            <div
              id="PaymentOptions-foreign"
              className={[
                'PaymentOptions-foreign',
                ...(foreignsVisible ? ['is-open'] : []),
              ].join(' ')}
            >
              {hasLocalOptions && (
                <button
                  onClick={handleShowForeignsClick}
                  className="PaymentOptions-showMore"
                  aria-controls="PaymentOptions-foreign"
                  aria-expanded={foreignsVisible}
                  data-testid="showMore"
                >
                  {i18n.t('paymentOptions.showMore')}
                </button>
              )}
              {(foreignsVisible || hasOnlyForeignOptions) &&
                renderForeignOptions()}
            </div>
          )}

          {isCouponEnabled && (
            <Coupon checked={isCouponChecked} onChange={handleCouponChange} />
          )}
        </div>

        <div className="pt-6 pb-8">
          <Terms.SinglePayment
            checked={!!values.terms}
            onChange={handleTermsChange}
            isOfferAstropay={isOfferAstropay}
          />
          <Button
            onClick={handleOnClick}
            tone="primary"
            size="full"
            data-testid="next"
            disabled={!isValid || isSubmitting}
            className="mt-4"
          >
            {isSubmitting ? (
              <LoadingDots />
            ) : (
              i18n.t('checkout.paymentOptions.next')
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
